import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Banner, Button, Checkbox, DataTable, DefinitionList, Dialog, EmptyState, Grid, Inline, Link, Menu, PageHeader, Panel, Skeleton, Stack, t, TabPanel, Tabs, Text, TextField, Timeline, toast,
} from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { EnvironmentBadge, ServerStateBadge } from '../components/ServerState';
import { OutsideScopeBanner, useDisplayScope } from '../components/ScopePicker';
import { RefusalPage } from '../components/Refusal';
import { api, ApiFailure, unwrap } from '../lib/api';
import { MaintenanceDialog } from '../components/MaintenanceDialog';
import { RunningPanel } from '../components/RunningPanel';
import { ConfigurationPanel, configurationKey, useServerConfiguration } from '../components/ConfigurationPanel';
import { InterruptionsPanel } from '../components/InterruptionsPanel';
import { SynchronisationPanel } from '../components/SynchronisationPanel';
import { EnrolmentBand } from '../components/enrolment/EnrolmentBand';
import { EnrolmentHealthNotice } from '../components/enrolment/EnrolmentHealthNotice';
import { ApplicationsPanel } from '../components/ApplicationsPanel';
import { ServerDnsNotice } from '../components/domains/ServerDnsNotice';
import { AddressTable } from '../components/domains/AddressTable';
import { RenameDialog, RenameProgress } from '../components/RenameDialog';
import { FAMILIES, familyOf, frenchName } from '../components/checkFamilies';
import { checkColumn } from '../components/StateColumn';
import { clockTime, durationSince, relativeTime, shortDate, shortDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useNarrow } from '../lib/useNarrow';
import { PendingButton } from '../components/PendingButton';
import { FULL_HEIGHT } from '../lib/tables';
import { changeRef, type PullRequest } from '../components/catalogue/PullRequestNotice';

type Check = fleetState.components['schemas']['Check'];
type Maintenance = fleetState.components['schemas']['Maintenance'];
type ServerAlert = fleetState.components['schemas']['Alert'];
const MAINTENANCE_TONES = { planned: 'info', active: 'info', lifted: 'neutral', finished: 'neutral' } as const;

/** A cause of the state: red for an incident or a security fix, orange for an update, grey for the rest. */
function reasonTone(code: string): 'neg' | 'warn' | 'neutral' {
  if (/^(HOST_DOWN|SERVICE_CRIT|DISK_CRIT|CERT_EXPIRED|APT_SECURITY|WP_VULNERABLE.*|PHP_SECURITY|PHP_EOL)$/.test(code)) return 'neg';
  if (/^(NO_DATA|ENROLLING|NOT_MONITORED|MAINTENANCE)$/.test(code)) return 'neutral';
  // ENROLLING_STALLED: Checkmk does not add the checks it found, someone has to look.
  return 'warn';
}

/** Checkmk's output said plainly (summary), else without its (!!) marks. */
const readable = (c: Check) => c.summary ?? c.output.replace(/\s*\(!!?\)/g, '').trim();

/**
 * Santé (D25, change of SPEC-004): what to deal with first — failing checks, in plain
 * words, with how long they have been failing — then the checks in order, one tab per family.
 */
function HealthChecks({ checks, loading, subtitle, actions }: { checks: Check[]; loading: boolean; subtitle: string; actions?: React.ReactNode }) {
  const [family, setFamily] = useState<string | null>(null);
  // D53: the state first, a dot only, the legend in its header.
  const stateColumn = checkColumn<Check>(c => c);
  const nameOf = (c: Check) => frenchName(c.name) ?? c.name;
  const failing = checks.filter(c => c.state === 'crit' || c.state === 'warn' || c.state === 'unknown');
  const fine = checks.filter(c => !failing.includes(c));
  const groups = FAMILIES.map(f => ({ f, items: fine.filter(c => familyOf(c.name) === f) })).filter(g => g.items.length);
  const active = groups.find(g => g.f === family) ?? groups[0];
  // One idea per column; Checkmk's own name and raw output stay a « Colonnes » click away.
  const checkmkColumns = [
    { key: 'checkmk', sortable: false, label: t('health.checkmkName'), mono: true, width: 220, hidden: true, accessor: (c: Check) => c.name },
    { key: 'output', sortable: false, label: t('health.rawOutput'), mono: true, width: 320, hidden: true },
  ];
  const toolbar = { toolbarSearch: false, toolbarFilters: false, toolbarCount: false, toolbarDensity: false } as const;
  return (
    <Stack gap={24}>
      <Panel title={t('health.toFix')} subtitle={subtitle} actions={actions} flush>
        {loading ? <Skeleton lines={4} />
          : !checks.length ? <EmptyState framed={false} icon="monitor_heart" title={t('server.health.empty.title')}>{t('server.health.empty.body')}</EmptyState>
            : failing.length > 0 ? (
              <DataTable
                bordered={false}
                height={FULL_HEIGHT}
                label={t('health.toFix')}
                rows={failing}
                rowKey="name"
                {...toolbar}
                density="compact"
                columns={[
                  stateColumn,
                  { key: 'name', sortable: false, label: t('server.health.columns.name'), pinned: true, width: 240, accessor: nameOf, strong: true },
                  { key: 'summary', sortable: false, label: t('health.cause'), grow: true, width: 280, accessor: readable },
                  { key: 'since', sortable: false, label: t('health.since'), width: 110, render: (c: Check) => (c.since ? durationSince(c.since) : '—') },
                  { key: 'lastCheckAt', sortable: false, label: t('server.health.columns.lastCheck'), width: 140, render: (c: Check) => (c.lastCheckAt ? relativeTime(c.lastCheckAt) : t('checks.notYet')) },
                  ...checkmkColumns,
                ]}
              />
            ) : <Alert tone="pos" title={t('health.allGood', { n: checks.length })} />}
      </Panel>
      {active && (
        <Panel title={t('health.inOrder')} subtitle={t('health.familyCount', { n: fine.length })}>
          <Stack gap={16}>
            <Tabs
              id="health-families"
              label={t('health.inOrder')}
              value={active.f}
              onChange={setFamily}
              items={groups.map(g => ({ id: g.f, label: t(`health.families.${g.f}`), count: g.items.length }))}
            />
            {groups.map(g => (
              <TabPanel key={g.f} id={g.f} tabsId="health-families" selected={g.f === active.f}>
                <DataTable
                  height={FULL_HEIGHT}
                  label={t(`health.families.${g.f}`)}
                  rows={g.items}
                  rowKey="name"
                  {...toolbar}
                  density="compact"
                  bordered
                  columns={[
                    stateColumn,
                    { key: 'name', sortable: false, label: t('server.health.columns.name'), pinned: true, width: 260, accessor: nameOf, strong: true },
                    { key: 'summary', sortable: false, label: t('health.result'), grow: true, width: 280, accessor: readable },
                    { key: 'lastCheckAt', sortable: false, label: t('server.health.columns.lastCheck'), width: 140, render: (c: Check) => (c.lastCheckAt ? relativeTime(c.lastCheckAt) : t('checks.notYet')) },
                    ...checkmkColumns,
                  ]}
                />
              </TabPanel>
            ))}
          </Stack>
        </Panel>
      )}
    </Stack>
  );
}

/** Server page (SPEC-004): record band with tabs; Overview and Health in this module. */
export function ServerPage() {
  const { server = '' } = useParams();
  const [params, setParams] = useSearchParams();
  // D27: « Ce qui tourne » became the Domaines tab and its neighbours.
  // D47: the former « Ce qui tourne », Conteneurs and Logiciels tabs are « Applications »; D82: Domaines is its own tab again.
  const askedTab = params.get('tab') ?? 'overview';
  const legacyTab = ['running', 'containers', 'offPackage'].includes(askedTab) ? 'applications' : askedTab;
  const { enums, can } = useSession();
  const navigate = useNavigate();
  const scope = useDisplayScope();
  const detail = useQuery({
    queryKey: ['server', server, scope],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}', { params: { path: { server }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });
  // A person restricted to a perimeter has no « Domaines » tab: its former address still opens « Applications ».
  const tab = legacyTab === 'domains' && detail.data && !detail.data.tabs.includes('domains') ? 'applications' : legacyTab;
  // D82: the « Domaines » tab counts the addresses in red or orange (DNS elsewhere or missing, certificate, domain name).
  const domains = useQuery({
    queryKey: ['addresses', 'count', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/addresses', { params: { query: { server, pageSize: 1 } } })),
    enabled: Boolean(detail.data?.tabs.includes('domains')),
  });
  const domainIssues = domains.data ? domains.data.views.urgent + domains.data.views.attention : 0;
  const history = useQuery({
    queryKey: ['server-maintenances', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/maintenances', { params: { path: { server } } })),
    enabled: tab === 'history' && detail.isSuccess,
  });
  const openAlerts = useQuery({
    queryKey: ['server-alerts', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/alerts', { params: { path: { server }, query: { status: 'open' } } })),
    enabled: detail.isSuccess,
    refetchInterval: 15_000,
    retry: false,
  });
  // SPEC-004 « Mobile < 600 px »: with an alert in progress, the overview leads with what the on-call person needs.
  const narrow = useNarrow();
  const alertsShown = [...(openAlerts.data?.items ?? [])].sort((a, b) => Number(Boolean(a.service)) - Number(Boolean(b.service)) || b.startedAt.localeCompare(a.startedAt));
  const phoneAlert = narrow && tab === 'overview' && alertsShown.length > 0;
  const checks = useQuery({
    queryKey: ['server-checks', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/checks', { params: { path: { server } } })),
    enabled: (tab === 'health' || phoneAlert) && detail.isSuccess,
    refetchInterval: 30_000,
  });
  const onCall = useQuery({
    queryKey: ['oncall-current'],
    queryFn: async () => unwrap(await api.maintenance.GET('/notifications/oncall/current')),
    enabled: phoneAlert,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const queryClient = useQueryClient();
  const [claiming, setClaiming] = useState(false);
  const claim = async (a: ServerAlert) => {
    setClaiming(true);
    try {
      unwrap(await api.fleetState.POST('/alerts/{alert}/claim', { params: { path: { alert: a.id } } }));
      toast({ tone: 'pos', title: t('alerts.toast.claimed') });
      void queryClient.invalidateQueries({ queryKey: ['server-alerts', server] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: e.body.code === 'CONFLICT' ? 'info' : 'neg', title: e.body.message, description: e.body.hint });
      void queryClient.invalidateQueries({ queryKey: ['server-alerts', server] });
    } finally {
      setClaiming(false);
    }
  };
  const [posting, setPosting] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [stepUp, setStepUp] = useState<'maintenance' | 'rename' | 'spare' | null>(null);
  const [renaming, setRenaming] = useState(false);
  // D22: the rename the portal proposes, and the one under way.
  const rename = useQuery({
    queryKey: ['server-rename', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/rename', { params: { path: { server } } })),
    refetchInterval: q => (q.state.data?.pullRequest && !['applied', 'closed'].includes(q.state.data.pullRequest.state) ? 30_000 : false),
  });
  // D77: « Mettre en réserve » / « Sortir de la réserve », confirmed, then a catalogue pull request.
  const [spareAsked, setSpareAsked] = useState<boolean | null>(null);
  const [spareSending, setSpareSending] = useState(false);
  // D80: the reserve pull request, from opened to read in the catalogue.
  const spareRequest = useQuery({
    queryKey: ['server-spare', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/spare', { params: { path: { server } } })),
    refetchInterval: query => (query.state.data?.pullRequest && query.state.data.pullRequest.state !== 'applied' && query.state.data.pullRequest.state !== 'closed' ? 30_000 : false),
  });
  // D84: into the reserve, a new name is proposed too: vps-<os>-spare-<4 digits>, free in the catalogue.
  const [spareRename, setSpareRename] = useState<{ on: boolean; name: string } | null>(null);
  const SERVER_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
  const spareName = () => {
    const os = (detail.data?.os ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const taken = new Set(rename.data?.taken ?? []);
    for (;;) {
      const name = `vps-${os ? `${os}-` : ''}spare-${String(Math.floor(1000 + Math.random() * 9000))}`;
      if (!taken.has(name)) return name;
    }
  };
  const askSpare = (spare: boolean, name?: string) => {
    setSpareRename(spare && rename.data?.canRename ? { on: true, name: name ?? spareName() } : null);
    setSpareAsked(spare);
  };
  // Back from the strong authentication a rename needs: the dialog again, with the name chosen.
  useEffect(() => {
    const asked = params.get('spareRename');
    if (asked && rename.data && detail.data) {
      askSpare(true, asked);
      setParams(p => { p.delete('spareRename'); return p; }, { replace: true });
    }
  }, [params, rename.data, detail.data]);
  const spareRenameTo = spareRename?.on ? spareRename.name.trim() : null;
  const spareRenameInvalid = spareRenameTo !== null && (!SERVER_NAME.test(spareRenameTo) || (rename.data?.taken ?? []).includes(spareRenameTo));
  const setSpare = async (spare: boolean) => {
    setSpareSending(true);
    try {
      const r = unwrap(await api.maintenance.PUT('/servers/{server}/spare', { params: { path: { server } }, body: { spare, ...(spare && spareRenameTo ? { rename: spareRenameTo } : {}) } }));
      toast({ tone: 'pos', title: t(spare ? 'spare.toast.opened' : 'spare.toast.openedOut', { server }), description: t('spare.toast.pullRequest', { ref: changeRef(r.pullRequest) }) });
      if (r.rename) toast({ tone: 'pos', title: t('rename.opened', { ref: changeRef(r.rename.pullRequest) }), description: t('spare.rename.toast', { to: r.rename.to }) });
      if (r.renameError) toast({ tone: 'warn', title: t('spare.rename.failed'), description: r.renameError });
      setSpareAsked(null);
      void spareRequest.refetch();
      void rename.refetch();
    } catch (e) {
      // A rename needs a recent strong authentication: nothing was written, the dialog comes back after it.
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') { setSpareAsked(null); setStepUp('spare'); }
      else if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint ?? (e.body.details?.fields as { message: string }[] | undefined)?.[0]?.message });
    } finally {
      setSpareSending(false);
    }
  };
  // D83: the platform's name written as the VPS name at OVHcloud, when the two differ.
  const [providerNameAsked, setProviderNameAsked] = useState(false);
  const [providerNameSending, setProviderNameSending] = useState(false);
  const pushProviderName = async () => {
    setProviderNameSending(true);
    try {
      const r = unwrap(await api.maintenance.POST('/servers/{server}/provider-name', { params: { path: { server } } }));
      toast({ tone: 'pos', title: t('providerName.toast', { name: r.name }), description: t(r.synced ? 'providerName.synced' : 'providerName.later') });
      setProviderNameAsked(false);
      // The OVHcloud sync reads the new name back within a minute or so.
      setTimeout(() => void detail.refetch(), 60_000);
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setProviderNameSending(false);
    }
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['server', server] });
    void queryClient.invalidateQueries({ queryKey: ['server-maintenances', server] });
    void queryClient.invalidateQueries({ queryKey: ['servers'] });
  };
  const lift = async (m: Maintenance) => {
    setLifting(true);
    try {
      unwrap(await api.fleetState.POST('/servers/{server}/maintenances/{maintenance}/end', { params: { path: { server, maintenance: m.id } }, body: {} }));
      toast({ tone: 'pos', title: t('maintenance.toast.lifted', { server }) });
      refresh();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') setStepUp('maintenance');
      else if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setLifting(false);
    }
  };
  // Ctrl K « Mettre en maintenance » opens the dialog, never an execution (RG-002-6).
  const asked = params.get('action') === 'maintenance';
  const allowed = Boolean(detail.data?.actions.includes('maintenance.create'));
  useEffect(() => {
    if (!asked || !detail.data) return;
    if (allowed && detail.data.maintenance?.status !== 'active') setPosting(true);
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
  }, [asked, allowed, detail.data, params, setParams]);
  const crumbs = [{ label: t('nav.servers'), href: '/servers' }, { label: server }];
  const rules = [...new Set((detail.data?.deployments ?? []).map(d => d.application))].sort();

  const configuration = useServerConfiguration(server, Boolean(detail.data?.tabs.includes('configuration')));
  // SPEC-034 § 6 (lot 1.5): the installations of the server (before any early return: hooks keep their order).
  const installs = useQuery({
    queryKey: ['server-installations', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/installations', { params: { path: { server } } })),
    retry: false,
  });
  if (detail.error) return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  const s = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;

  const canMaintain = Boolean(s?.actions.includes('maintenance.create'));
  const maintenance = s?.maintenance ?? null;
  // D48: on the Applications tab, the tab's action is « Déclarer une application » (one primary per view).
  const canDeclare = Boolean(s && !s.archived && can('project.servers.update'));
  const tabAction = tab === 'applications' && canDeclare
    ? <Button size="sm" icon="add" onClick={() => navigate(`/servers/${encodeURIComponent(server)}/declare`)}>{t('declare.action')}</Button>
    : !canMaintain || !s ? undefined
    : maintenance?.status === 'active'
      ? <PendingButton variant="secondary" size="sm" icon="build" pending={lifting} onClick={() => void lift(maintenance)}>{t('maintenance.lift')}</PendingButton>
      : <Button size="sm" icon="build" onClick={() => setPosting(true)}>{t('maintenance.post')}</Button>;

  // D30: « Mettre à jour la configuration » in the header, with what waits; « Enrôler » before a first enrolment.
  const cfg = configuration.data;
  const configAction = cfg?.canEnrol ? (
    <Button variant="secondary" size="sm" icon="system_update_alt" onClick={() => navigate(`/servers/import?servers=${encodeURIComponent(server)}`)}>
      {cfg.enrolled ? (cfg.pending ? t('configuration.updateN', { n: cfg.pending }) : t('configuration.update')) : t('configuration.enrol')}
    </Button>
  ) : undefined;

  // SPEC-034 § 6 (lot 1.5): « Installer une application » on a reserve server, or on one where the platform already
  // installed an application of the project (D102); or follow the installation under way. The assistant can create
  // the application and its environment: no waiting deployment of the catalogue is needed.
  const running = installs.data?.items.find(i => ['reserved', 'preparing', 'prepared', 'catalogued', 'ready', 'deploying', 'deployed'].includes(i.state) || (i.state === 'failed' && !['retire', 'unlink', 'purge', 'reset'].includes(i.failedStep ?? '')));
  const installAction = running
    ? <Button variant="secondary" size="sm" icon="deployed_code" onClick={() => navigate(`/servers/${encodeURIComponent(server)}/install?installation=${encodeURIComponent(running.id)}${running.batch ? `&batch=${encodeURIComponent(running.batch)}` : ''}`)}>{t('servers.installFollow')}</Button>
    : installs.data?.canInstall
      ? <Button variant="secondary" size="sm" icon="deployed_code" onClick={() => navigate(`/servers/${encodeURIComponent(server)}/install`)}>{t('servers.install')}</Button>
      : undefined;

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.server')}
      title={server}
      actions={configAction && installAction ? <Inline gap={8}>{installAction}{configAction}</Inline> : installAction ?? configAction}
      extra={cfg ? <EnrolmentBand server={server} jobs={cfg.jobs} onFinished={() => { refresh(); void queryClient.invalidateQueries({ queryKey: configurationKey(server) }); }} /> : undefined}
      status={s ? <ServerStateBadge state={s.state} security={s.security} /> : undefined}
      subtitle={s ? [s.placement.label, label(enums.providers, s.hosting.provider), s.hosting.region, s.os ?? undefined].filter(Boolean).join(' · ') : undefined}
      tabs={{
        label: t('server.tabs.label'),
        value: tab,
        onChange: id => setParams(id === 'overview' ? {} : { tab: id }),
        items: (s?.tabs ?? ['overview', 'health']).map(id => ({ id, label: t(`server.tabs.${id}`), count: (id === 'domains' ? domainIssues : s?.tabCounts[id]) || undefined })),
        action: tabAction,
      }}
    />
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!s ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="server" outside={s.outsideDisplayScope} />
          {s.archived && <Alert tone="info">{t('server.archived')}</Alert>}
          {rename.data && <RenameProgress rename={rename.data} />}
          {spareRequest.data?.pullRequest && <SpareProgress request={spareRequest.data.pullRequest} />}
          {/* SPEC-004 « Alerte en cours »: one alert, the host's first, else the latest; the others are counted. */}
          {alertsShown[0] && (
            <AlertInProgress
              key={alertsShown[0].id} alert={alertsShown[0]} others={alertsShown.length - 1}
              onOthers={() => setParams({ tab: 'health' })}
              onUpdates={() => setParams({ tab: 'updates' })}
              canClaim={Boolean(s.actions.includes('alert.claim'))} claiming={claiming} onClaim={() => void claim(alertsShown[0]!)}
            />
          )}
          {maintenance && (
            <Banner
              tone="info"
              title={maintenance.status === 'active'
                ? t('maintenance.banner.active', { until: clockTime(maintenance.end) })
                : t('maintenance.banner.planned', { date: shortDate(maintenance.start), time: clockTime(maintenance.start), until: clockTime(maintenance.end) })}
              actions={canMaintain ? <PendingButton variant="secondary" size="sm" pending={lifting} onClick={() => void lift(maintenance)}>{maintenance.status === 'active' ? t('maintenance.lift') : t('maintenance.cancelPlanned')}</PendingButton> : undefined}
            >
              {t('maintenance.banner.body', { reason: maintenance.reason, by: maintenance.createdBy.name })}
            </Banner>
          )}
          {tab === 'overview' && phoneAlert && (
            <PhoneAlert
              alert={alertsShown[0]!}
              failing={(checks.data?.items ?? []).filter(c => c.state !== 'ok' && c.state !== 'pending')}
              loading={checks.isPending}
              provider={label(enums.providers, s.hosting.provider)}
              providerState={s.hosting.state ?? null}
              onMaintain={canMaintain && maintenance?.status !== 'active' ? () => setPosting(true) : undefined}
              phone={onCall.data?.phone ?? null}
            />
          )}
          {tab === 'overview' && !phoneAlert && (
            <Grid columns="2-1">
              {/* D115: what needs an action (the causes of its state, its applications' defects, its retired
                  deployments), each with the way to the tab that treats it; its applications are their own tab. */}
              <Panel title={t('application.summary.title')}>
                {(() => {
                  const retired = (installs.data?.items ?? []).filter(i => ['retire_requested', 'retiring', 'unlinking', 'retired'].includes(i.state));
                  const tabOf = (code: string) => (/^APT_/.test(code) ? 'updates' : 'health');
                  const items = [
                    ...s.reasons.map(r => (
                      <Alert key={`${r.code}-${r.label}`} tone={((tone: ReturnType<typeof reasonTone>) => (tone === 'neutral' ? 'info' : tone))(reasonTone(r.code))}
                        title={r.label} action={t(`server.issues.open.${tabOf(r.code)}`)} onAction={() => setParams({ tab: tabOf(r.code) })}>
                        {r.since ? t('health.sinceFor', { d: durationSince(r.since) }) : undefined}
                      </Alert>
                    )),
                    // D47: the applications' defects, apart from the server's state, one click from their tab.
                    ...(s.applicationDefects.length ? [
                      <Alert key="defects" tone="warn" title={t('applications.defects.title', { n: s.applicationDefects.length })} action={t('applications.defects.open')} onAction={() => setParams({ tab: 'applications' })}>
                        <Stack gap={4}>{s.applicationDefects.map(d => <Text key={`${d.code}:${d.label}`} variant="body-sm">{d.label}</Text>)}</Stack>
                      </Alert>,
                    ] : []),
                    // SPEC-034 § 12.4: a retired deployment's volumes stay here until the grace period ends.
                    ...(retired.length ? [
                      <Alert key="retired" tone="info" title={t('server.deployments.retiredTitle', { n: retired.length })}>
                        <Stack gap={4}>
                          {retired.map(i => (
                            <Inline key={i.id} gap={8} align="center">
                              <Link href={`/environments/${encodeURIComponent(i.deployment)}`} onClick={(e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/environments/${encodeURIComponent(i.deployment)}`); }}>
                                {i.deployment}
                              </Link>
                              <Text variant="body-sm" tone="ink-2">
                                {i.state === 'retired' && i.graceUntil ? t('server.deployments.retiredUntil', { until: shortDate(i.graceUntil) }) : t('server.deployments.retiringShort')}
                              </Text>
                            </Inline>
                          ))}
                        </Stack>
                      </Alert>,
                    ] : []),
                  ];
                  return items.length ? <Stack gap={12}>{items}</Stack> : <Alert tone="pos" title={t('application.summary.allGood')}>{t('server.issues.allGoodBody')}</Alert>;
                })()}
              </Panel>
              <Panel
                title={t('application.properties')}
                // SPEC-007 Rattachement: environment, team and criticality change by pull request (D23).
                // D22 « Renommer »: the VPS at OVHcloud, the Checkmk host and the catalogue take the new name.
                actions={!s.archived && (s.actions.includes('server.import') || rename.data?.canRename) ? (
                  <Menu
                    label={t('server.summary.edit')}
                    align="end"
                    trigger={<Button variant="ghost" size="sm" icon="edit">{t('server.summary.edit')}</Button>}
                    items={[
                      ...(s.actions.includes('server.import') ? [{ label: t('server.summary.editAttachment'), onSelect: () => navigate(`/servers/import?servers=${encodeURIComponent(s.name)}&step=attachment`) }] : []),
                      ...(rename.data?.canRename ? [{ label: t('rename.action'), onSelect: () => setRenaming(true) }] : []),
                      // D77: a server that hosts nothing can wait in reserve; out of it, or by joining an application.
                      ...(s.actions.includes('server.import') && (s.spare || (s.deployments.length === 0 && s.hiddenDeployments === 0))
                        ? [{ label: t(s.spare ? 'spare.out' : 'spare.in'), onSelect: () => askSpare(!s.spare) }] : []),
                    ]}
                  />
                ) : undefined}
              >
                <DefinitionList
                  stacked
                  items={[
                    { label: t('server.summary.hosting'), value: `${label(enums.providers, s.hosting.provider)} · ${s.hosting.region}` },
                    // D83: the VPS name in the OVHcloud console, and the platform's written there when they differ.
                    ...(s.hosting.provider === 'ovhcloud' && s.hosting.ref ? [{
                      label: t('providerName.label'),
                      value: (
                        <Stack gap={4}>
                          <Text variant="code">{s.hosting.name ?? t('servers.noData')}</Text>
                          {s.hosting.name !== s.name && (
                            <Inline gap={8} wrap>
                              <Badge tone="warn" size="sm">{t('providerName.mismatch')}</Badge>
                              {s.actions.includes('server.rename') && !s.archived && (
                                <Button variant="secondary" size="sm" icon="sync" onClick={() => setProviderNameAsked(true)}>{t('providerName.push')}</Button>
                              )}
                            </Inline>
                          )}
                        </Stack>
                      ),
                    }] : []),
                    { label: t('server.summary.ip'), value: s.hosting.ip ?? t('servers.noData'), mono: true },
                    { label: t('server.summary.os'), value: s.os ?? t('servers.noData') },
                    {
                      label: t('server.summary.environment'),
                      value: s.environmentUnset
                        ? <Stack gap={4}><Inline><EnvironmentBadge environment={s.environment} unset /></Inline><Text variant="caption" tone="mute">{t('server.summary.environmentUnset')}</Text></Stack>
                        : <EnvironmentBadge environment={s.environment} />,
                    },
                    ...(s.spare ? [{ label: t('spare.label'), value: <Stack gap={4}><Inline><Badge tone="info" size="sm">{t('spare.badge')}</Badge></Inline><Text variant="caption" tone="mute">{t('spare.summary')}</Text></Stack> }] : []),
                    { label: t('server.summary.criticality'), value: label(enums.criticalities, s.criticality) },
                    // D115: its applications are their own tab; here how many, and those of other clients counted only.
                    { label: t('server.summary.applications'), value: s.hiddenDeployments > 0 ? t('server.summary.applicationsHidden', { n: s.deployments.length, hidden: s.hiddenDeployments }) : String(s.deployments.length) },
                    { label: t('server.summary.team'), value: s.team },
                    { label: t('server.summary.updates'), value: s.pendingUpdates ?? t('servers.noData') },
                    { label: t('server.summary.lastData'), value: s.lastDataAt ? `${clockTime(s.lastDataAt)} · ${relativeTime(s.lastDataAt)}` : t('servers.noData') },
                  ]}
                />
              </Panel>
            </Grid>
          )}
          {/* D68: why the agent's checks are « À traiter » while the server is being enrolled. */}
          {tab === 'health' && cfg && (
            <EnrolmentHealthNotice server={server} jobs={cfg.jobs} canEnrol={cfg.canEnrol && !s.archived}
              onRetry={() => navigate(`/servers/import?servers=${encodeURIComponent(server)}`)} />
          )}
          {tab === 'health' && (
            <HealthChecks
              checks={checks.data?.items ?? []}
              loading={checks.isPending}
              subtitle={t('server.health.subtitle', { when: relativeTime(checks.data?.lastDataAt ?? s.lastDataAt) })}
              actions={rules.length > 0 ? (
                // SPEC-008: the rules of each application this server carries.
                <Menu label={t('server.health.rules')} align="end" trigger={<Button variant="secondary" size="sm" icon="tune">{t('server.health.rules')}</Button>}
                  items={rules.map(application => ({ label: application, onSelect: () => navigate(`/applications/${encodeURIComponent(application)}?tab=monitoring`) }))} />
              ) : undefined}
            />
          )}
          {/* D47, D49: the deployments the server carries in one table; the undeclared ones in a drawer. */}
          {/* D78, D82: the addresses it serves, those that no longer point to it first. */}
          {tab === 'domains' && (
            <Stack gap={16}>
              <ServerDnsNotice server={s.name} />
              <AddressTable server={s.name} />
            </Stack>
          )}
          {tab === 'applications' && (
            <ApplicationsPanel
              server={s.name}
              deployments={s.deployments}
              defects={s.applicationDefects}
              canReapply={!s.archived}
              onReapply={() => navigate(`/servers/import?servers=${encodeURIComponent(s.name)}`)}
              {...(canDeclare ? { onDeclare: (group?: string) => navigate(`/servers/${encodeURIComponent(server)}/declare${group ? `?group=${encodeURIComponent(group)}` : ''}`) } : {})}
            />
          )}
          {(tab === 'updates' || tab === 'ports') && (
            // SPEC-032, one tab per section (D27). « Réappliquer la configuration » reruns the enrolment, reviewed at the Accès step (D24).
            <RunningPanel server={s.name} section={tab} canReapply={!s.archived} onReapply={() => navigate(`/servers/import?servers=${encodeURIComponent(s.name)}`)} />
          )}
          {tab === 'configuration' && <ConfigurationPanel server={s.name} />}
          {tab === 'synchronisation' && <SynchronisationPanel server={s.name} />}
          {tab === 'history' && <InterruptionsPanel server={s.name} />}
          {tab === 'history' && (
            <Panel title={t('server.history.maintenances')} subtitle={t('server.history.subtitle')} flush>
              <DataTable
                bordered={false}
                height={FULL_HEIGHT}
                label={t('server.history.maintenances')}
                rows={history.data?.items ?? []}
                rowKey="id"
                loading={history.isPending}
                toolbar={false}
                density="compact"
                columns={[
                  { key: 'status', label: t('server.history.columns.status'), width: 120, render: (m: Maintenance) => <Badge tone={MAINTENANCE_TONES[m.status]}>{t(`maintenance.status.${m.status}`)}</Badge> },
                  { key: 'start', label: t('server.history.columns.window'), width: 170, render: (m: Maintenance) => `${shortDateTime(m.start)} → ${clockTime(m.liftedAt ?? m.end)}` },
                  { key: 'reason', label: t('server.history.columns.reason'), grow: true, width: 240 },
                  { key: 'createdBy', label: t('server.history.columns.by'), width: 220, render: (m: Maintenance) => (m.liftedBy ? t('server.history.byLifted', { by: m.createdBy.name, lifted: m.liftedBy }) : m.createdBy.name) },
                ]}
                empty={<EmptyState icon="build" title={t('server.history.empty')} />}
              />
            </Panel>
          )}
        </Stack>
      )}
      {s && (
        <MaintenanceDialog
          open={posting}
          targets={[{ name: s.name, environment: s.environment, team: s.team }]}
          environmentLabel={env => label(enums.environments, env)}
          onClose={() => setPosting(false)}
          onDone={refresh}
          onStepUp={() => { setPosting(false); setStepUp('maintenance'); }}
        />
      )}
      {renaming && rename.data && (
        <RenameDialog
          rename={rename.data}
          onClose={() => setRenaming(false)}
          onOpened={() => { setRenaming(false); void rename.refetch(); }}
          onStepUp={() => { setRenaming(false); setStepUp('rename'); }}
        />
      )}
      <Dialog
        open={spareAsked !== null}
        onClose={() => setSpareAsked(null)}
        title={t(spareAsked ? 'spare.dialog.inTitle' : 'spare.dialog.outTitle', { server })}
        description={t(spareAsked ? 'spare.dialog.inBody' : 'spare.dialog.outBody')}
        confirmLabel={t(spareAsked ? (spareRenameTo ? 'spare.inRename' : 'spare.in') : 'spare.out')}
        confirmDisabled={spareSending || spareRenameInvalid}
        onConfirm={() => { if (spareAsked !== null) void setSpare(spareAsked); }}
      >
        {spareAsked && spareRename && (
          <Stack gap={12}>
            <Checkbox checked={spareRename.on} onChange={(on: boolean) => setSpareRename({ ...spareRename, on })} label={t('spare.rename.label')} />
            {spareRename.on && (
              <TextField
                label={t('rename.name')}
                value={spareRename.name}
                mono
                onChange={(e: { target: { value: string } }) => setSpareRename({ ...spareRename, name: e.target.value.toLowerCase() })}
                hint={t('spare.rename.hint', { ref: rename.data?.providerRef ?? '—' })}
                error={spareRenameInvalid ? ((rename.data?.taken ?? []).includes(spareRenameTo ?? '') ? t('rename.taken', { name: spareRenameTo ?? '' }) : t('rename.invalid')) : undefined}
                required
              />
            )}
          </Stack>
        )}
      </Dialog>
      <Dialog
        open={providerNameAsked}
        onClose={() => setProviderNameAsked(false)}
        title={t('providerName.dialog.title', { name: server })}
        description={t('providerName.dialog.body', { from: s?.hosting.name ?? '—', to: server, ref: s?.hosting.ref ?? '' })}
        confirmLabel={t('providerName.push')}
        confirmDisabled={providerNameSending}
        onConfirm={() => void pushProviderName()}
      />
      <Dialog
        open={stepUp !== null}
        onClose={() => setStepUp(null)}
        title={t('settings.enrolment.stepUp.title')}
        description={t(stepUp === 'rename' || stepUp === 'spare' ? 'rename.stepUp' : 'maintenance.stepUp')}
        confirmLabel={t('settings.enrolment.stepUp.confirm')}
        onConfirm={() => window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent(`/servers/${server}${stepUp === 'spare' && spareRenameTo ? `?spareRename=${encodeURIComponent(spareRenameTo)}` : ''}`)}`)}
      />
    </Shell>
  );
}

/** SPEC-004 « Alerte en cours »: since when, escalation countdown, « Je prends ». */
/** D29: Checkmk's « 7 normal updates(!), 0 security updates » read as people say it. */
function aptSummary(output: string): string | null {
  const m = output.match(/(\d+)\s+normal updates?.*?(\d+)\s+security updates?/i);
  if (!m) return null;
  const normal = Number(m[1]), security = Number(m[2]);
  return `${t('running.updates.count', { n: normal + security })} · ${security ? t('running.updates.withSecurity', { n: security }) : t('running.updates.noSecurity')}`;
}

function AlertInProgress({ alert, canClaim, claiming, onClaim, others = 0, onOthers, onUpdates }: { alert: ServerAlert; canClaim: boolean; claiming: boolean; onClaim: () => void; others?: number; onOthers?: () => void; onUpdates?: () => void }) {
  const minutes = alert.escalation ? Math.max(0, Math.ceil((new Date(alert.escalation.at).getTime() - Date.now()) / 60_000)) : null;
  // D29: a warning reads as a warning (orange, « en avertissement »); « en échec » and red are for critical.
  const warning = alert.severity === 'warning';
  const service = alert.service ? frenchName(alert.service) ?? alert.service : null;
  const apt = alert.service && /^APT\b/i.test(alert.service) ? aptSummary(alert.output) : null;
  const title = apt
    ? t('alerts.titleUpdates', { since: clockTime(alert.startedAt) })
    : service
    ? t(warning ? 'alerts.titleServiceWarn' : 'alerts.titleService', { service, since: clockTime(alert.startedAt) })
    : t('alerts.titleHost', { since: clockTime(alert.startedAt) });
  const taken = alert.claimedBy;
  const escalation = !alert.escalation ? null
    : alert.escalation.status === 'cancelled' ? t('alerts.escalation.cancelled')
      : alert.escalation.status === 'escalated' ? t('alerts.escalation.escalated')
        : t('alerts.escalation.in', { n: minutes ?? 0 });
  return (
    <Alert
      tone={taken ? 'info' : warning ? 'warn' : 'neg'}
      title={title}
      // DSM Alert: the action is a label, the Alert draws its button.
      action={!taken && canClaim && !claiming ? t('alerts.claim') : undefined}
      onAction={onClaim}
    >
      {[apt ?? alert.output, taken ? t('alerts.claimedBy', { name: taken.name, time: clockTime(alert.claimedAt!) }) : t('alerts.notClaimed'), escalation].filter(Boolean).join(' · ')}
      {apt && onUpdates && (
        <>
          {' · '}
          <Link href="?tab=updates" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); onUpdates(); }}>{t('alerts.seePackages')}</Link>
        </>
      )}
      {others > 0 && (
        <>
          {' · '}
          <Link href="?tab=health" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); onOthers?.(); }}>{t('alerts.others', { n: others })}</Link>
        </>
      )}
    </Alert>
  );
}

/**
 * SPEC-004 « Mobile < 600 px », an alert in progress: failing checks only, the state at the provider,
 * the chronology, then « Poser une maintenance » and « Appeler l'astreinte ».
 */
function PhoneAlert({ alert, failing, loading, provider, providerState, onMaintain, phone }: {
  alert: ServerAlert; failing: Check[]; loading: boolean; provider: string; providerState: string | null; onMaintain?: () => void; phone: string | null;
}) {
  const steps = [
    { id: 'opened', title: t('server.phone.opened'), when: clockTime(alert.startedAt), dateTime: alert.startedAt, status: 'done' as const },
    ...(alert.escalation?.status === 'escalated' ? [{ id: 'escalated', title: t('alerts.escalation.escalated'), when: clockTime(alert.escalation.at), dateTime: alert.escalation.at, status: 'error' as const }] : []),
    alert.claimedBy
      ? { id: 'claimed', title: t('alerts.claimedBy', { name: alert.claimedBy.name, time: clockTime(alert.claimedAt!) }), when: clockTime(alert.claimedAt!), dateTime: alert.claimedAt!, status: 'done' as const }
      : { id: 'claimed', title: t('alerts.notClaimed'), status: 'current' as const },
  ];
  return (
    <Stack gap={16}>
      <Panel title={t('server.phone.failing')} flush>
        <DataTable
          bordered={false}
          height={FULL_HEIGHT}
          label={t('server.phone.failing')}
          rows={failing}
          rowKey="name"
          loading={loading}
          toolbar={false}
          density="compact"
          columns={[
            checkColumn<Check>(c => c),
            { key: 'name', label: t('server.health.columns.name'), grow: true, accessor: (c: Check) => frenchName(c.name) ?? c.name },
          ]}
          empty={<EmptyState icon="monitor_heart" title={t('server.phone.noFailing')} />}
        />
      </Panel>
      <Panel title={t('server.phone.provider')}>
        <DefinitionList items={[{ label: provider, value: providerState ?? t('servers.noData'), mono: Boolean(providerState) }]} />
      </Panel>
      <Panel title={t('server.phone.timeline')}>
        <Timeline compact label={t('server.phone.timeline')} items={steps} />
      </Panel>
      <Inline gap={16} align="center">
        {onMaintain && <Button variant="secondary" icon="build" onClick={onMaintain}>{t('maintenance.post')}</Button>}
        {phone && <Link href={`tel:${phone}`} icon="call">{t('server.phone.call')}</Link>}
      </Inline>
    </Stack>
  );
}

/** D80: where « Mettre en réserve » / « Sortir de la réserve » stands: in review, check failed, merged and waiting for the catalogue, done. */
function SpareProgress({ request }: { request: PullRequest & { spare: boolean } }) {
  if (request.state === 'closed') return null;
  const way = request.spare ? 'in' : 'out';
  const link = <Link href={request.url} external>{t('pullRequest.link', { ref: changeRef(request) })}</Link>;
  const tone = request.state === 'validation_failed' ? 'warn' : request.state === 'applied' ? 'pos' : 'info';
  const state = request.state === 'open' && request.autoMerge ? 'autoMerge' : request.state;
  return (
    <Alert tone={tone} title={t(`spare.progress.${way}.${state}`)}>
      <Stack gap={4}>{request.ciMessage && request.state === 'validation_failed' && <Text variant="body-sm">{request.ciMessage}</Text>}{link}</Stack>
    </Alert>
  );
}
